import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { useGetTPAByIdQuery, usePatchTPAByIdMutation } from "services/tpa-api";

import { useSelector } from "utils/redux";
import { getTPAForm } from "forms/tpa";
import { isFetchBaseQueryError } from "utils/error-utils";

import InsurerTPAPageContainer from "../insurer/InsurerTPAPageContainer";
import KaboodleForm from "components/Forms/KaboodleForm";

import { setIsLoading } from "reducers/main";
import { useDispatch } from "utils/redux";

const TPAForm = () => {
  const dispatch = useDispatch();
  const { tpaId } = useParams<{ tpaId?: string }>();

  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const isReadOnly = !loggedInUser?.has_operations_authority;

  const {
    data: tpa,
    isLoading,
    refetch: refetchTPA,
  } = useGetTPAByIdQuery({ id: tpaId as string }, { skip: !tpaId });

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [patchTPAById] = usePatchTPAByIdMutation();

  const { reset, clearErrors, setError } = useFormContext();

  const constants = useSelector((state) => state.shared.constants);

  useEffect(() => {
    if (tpa) {
      reset(tpa);
    }
    refetchTPA();
  }, [tpa, reset]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (isReadOnly) return;
      if (tpaId) {
        try {
          await patchTPAById({
            id: tpaId,
            data: { [field]: value },
          }).unwrap();
          clearErrors(field);
        } catch (error: unknown) {
          if (isFetchBaseQueryError(error)) {
            const data = error.data as Record<string, string[]> | undefined;
            setError(field, {
              type: String(error.status),
              message: data?.[field]?.[0] || "Unable to save",
            });
          } else {
            setError(field, { message: "Unable to save" });
          }
        }
      }
    },
    [patchTPAById, tpaId, clearErrors, setError, isReadOnly],
  );

  const tpaFormContent = useMemo(() => {
    const forms = getTPAForm(constants, isReadOnly);
    return <KaboodleForm cards={forms[0].cards || []} onSave={handleSave} />;
  }, [constants, handleSave, isReadOnly]);

  return <InsurerTPAPageContainer>{tpaFormContent}</InsurerTPAPageContainer>;
};

export default TPAForm;
